import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { ErrorState } from '@adornis/components/client/ErrorState.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { AddressForm } from '@martinus/address/client/AddressForm.js';
import { Adresse } from '@martinus/address/entities/Adresse.js';
import { PageFooter } from '@martinus/styles/client/PageFooter.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { openValidationErrorDialog } from '@martinus/validation/sg-validation-error-dialog.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { LastEditorInformation } from '../../../history/client/LastEditorInformation.js';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import { SepaAPI } from '../../../sepa/api/SepaAPI.js';
import { SgUserGroupMemberships } from '../../details/sg-user-group-memberships.js';
import { Person } from '../../entities/person.js';
import { PersonRelations } from '../../family/client/PersonRelations.js';
import type { UserOnUserPayload } from '../../UserPolicy.js';
import { BankDaten } from '../entities/BankDaten.js';
import { ProfilDaten } from '../entities/ProfilDaten.js';
import { StammDaten } from '../entities/StammDaten.js';
import { UserProfileActions } from '../UserProfilePolicy.js';
import { BankDatenForm } from './BankDatenForm.js';
import { getReligionFields, StammdatenForm, StammdatenFormValidator } from './StammdatenForm.js';
import { UserInternalIdentifiers } from './UserInternalIdentifiers.js';

export const SgUserProfile = stateful((props: { personID: Maybe<string> }) => {
  if (!props.personID) throw new Error('No user given');
  const user = useObservable(() => currentUser<Person>(), []);
  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({
        _id: 1,
        username: 1,
        email: 1,
        externalUniqueID: 1,
        emailConfirmed: 1,
        lastConfirmationEmailSent: 1,
        sorgeberechtigteKinderIDs: 1,
        spendenKinderIDs: 1,
        debitorennummer: 1,
        legacyDebitorNumbers: { debitorNumber: 1, scope: 1 },
        nameAbbreviation: 1,
        roles: { name: 1, contexts: 1, permissions: 1 },
        profilDaten: {
          stammdaten: selectionSet(() => StammDaten, 1),
          adresse: selectionSet(() => Adresse, 1),
          bankInfos: selectionSet(() => BankDaten, 1),
        },
      }),
    [props.personID],
  );

  const {
    can: canViewProfile,
    reason,
    loading: permissionLoading,
  } = useMartinusABAC<UserOnUserPayload>({ action: UserProfileActions.VIEW_PROFILE, viewedUser: person });

  const { can: canViewProfileSensitiveData } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.VIEW_SENSITIVE_DATA,
    viewedUser: person,
  });

  const { can: canViewBankdata } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.VIEW_BANK_DATA,
    viewedUser: person,
  });

  const { can: canEditProfile } = useMartinusABAC<UserOnUserPayload>({
    action: UserProfileActions.EDIT_PROFILE,
    viewedUser: person,
  });

  // Only gate on data that is strictly required to render anything. Permission loading states
  // are intentionally NOT part of this guard: re-evaluating permissions (e.g. after a save)
  // briefly flips them to `loading: true`, and tearing down the whole card tree here would
  // cause the ToggleContentCards to lose their internal state (and scroll to jump).
  if (!user || personLoading || !person)
    return PageLoader({
      cardOptions: [
        canViewProfile ? { title: 'Stammdaten' } : {},
        canViewProfileSensitiveData ? { title: 'Adresse' } : {},
        canViewBankdata ? { title: 'Bankdaten' } : {},
        canViewProfileSensitiveData ? { title: 'Buchungen & Mitgliedschaften' } : {},
      ],
    });

  // Only show the no-permission state once permissions have actually finished loading.
  // While loading, we keep the previous (stale) permission values so the tree stays mounted.
  if (!canViewProfile && !permissionLoading) {
    return ErrorState({
      icon: 'icon-[material-symbols--lock-outline]',
      title: 'Keine Zugriffsrechte',
      description: reason ?? 'Sie haben nicht die nötigen Berechtigungen, um auf diese Seite zuzugreifen.',
    });
  }

  /**
   * Cards state persisted so we can control them for errors
   */
  const [cardOpenStates, setCardOpenStates] = useState({
    stammdaten: true,
    adresse: false,
    bankInfos: false,
    buchungen: false,
  });
  const setCardOpen = (card: keyof typeof cardOpenStates, open: boolean) => {
    setCardOpenStates({ ...cardOpenStates, [card]: open });
  };

  /**
   * We need this as the deep change listening on the form clashes with the proxies set by adornisentity
   * TODO: remove this after the baseql rework
   */
  const [personObject] = useState(person.toObject(), [person._id]);

  const form = useForm(
    personObject,
    {
      enabledFields: canEditProfile ? null : {},
      validator: {
        profilDaten: {
          stammdaten: StammdatenFormValidator(getReligionFields(personObject)),
          adresse: {
            streetAndNumber: sgNonOptional(),
            zip: sgNonOptional(),
            city: sgNonOptional(),
            country: sgNonOptional(),
          },
        },
      },
    },
    [canEditProfile],
  );

  const handleSave = async () => {
    /**
     * Ensure some fields
     */
    form.document.profilDaten ??= new ProfilDaten({});
    form.document.profilDaten.adresse ??= new Adresse({});
    form.document.profilDaten.adresse.country ??= 'DE';

    if (form.document.profilDaten.bankInfos) {
      form.document.profilDaten.bankInfos.legacyIbans ??= [];
      form.document.profilDaten.bankInfos.legacyIbans = form.document.profilDaten.bankInfos.legacyIbans.filter(Boolean);
    }

    /**
     * Add the iban to the legacyIbans if the user changed it
     */
    if (
      form.document.profilDaten.bankInfos?.iban &&
      person.profilDaten?.bankInfos?.iban &&
      person.profilDaten.bankInfos.iban !== form.document.profilDaten.bankInfos.iban
    ) {
      form.document.profilDaten.bankInfos.legacyIbans?.push(person.profilDaten.bankInfos.iban);
      form.document.profilDaten.bankInfos.legacyIbans = Array.from(
        new Set(form.document.profilDaten.bankInfos.legacyIbans),
      );
    }

    /**
     * Validate and open errounes sections if the user decides he wants to continue editing
     */
    const error = await form.validate();
    if (error && !(await openValidationErrorDialog({ error }))) {
      /**
       * Parse to array from multiple error
       */
      const validationErrors = error.extensions?.validationErrors as
        Array<{ extensions?: { path?: (string | number)[] } }> | undefined;

      const sectionHasError = (section: string) =>
        validationErrors?.some(e => e.extensions?.path?.includes(section)) ?? false;

      setCardOpenStates({
        stammdaten: sectionHasError('stammdaten'),
        adresse: sectionHasError('adresse'),
        bankInfos: sectionHasError('bankInfos'),
        buchungen: false,
      });
      return;
    }

    await guardDialog(
      async () => {
        /**
         * Check if the user has still open ibans
         */
        if (!form.document.profilDaten?.bankInfos?.iban && person.profilDaten?.bankInfos?.iban) {
          const hasSepa = await SepaAPI.checkSepa(person._id, person.profilDaten.bankInfos.iban, false)();
          if (
            hasSepa &&
            (await confirmDialog('Es sind noch SEPA-Mandate mit dieser IBAN aktiv. Wollen Sie diese sofort beenden?', {
              acceptText: 'Alle beenden',
              declineText: 'Nur die IBAN löschen',
            }))
          ) {
            await SepaAPI.checkSepa(person._id, person.profilDaten.bankInfos.iban, true)();
          }
        }

        await form.uploadAllPendingFiles();
        const value = constructValue(form.document) as Person;
        await value.save();
      },
      { successText: 'Person gespeichert' },
    );
  };

  return html`
    <div class="card-container">
      ${UserInternalIdentifiers({ form })}
      ${
        form.document.profilDaten?.stammdaten
          ? Collapse({
              heading: 'Stammdaten',
              open: cardOpenStates.stammdaten,
              onToggle: open => setCardOpen('stammdaten', open),
              content: StammdatenForm({ form }),
            })
          : canEditProfile
            ? html`
                <button
                  class="btn btn-dash btn-block justify-start"
                  @click=${() => {
                    if (!form.document.profilDaten) return;
                    form.document.profilDaten.stammdaten = new StammDaten({});
                    setCardOpen('stammdaten', true);
                  }}
                >
                  <span class="icon-[material-symbols--add] text-success"></span>
                  Stammdaten hinzufügen
                </button>
              `
            : nothing
      }
      ${
        canViewProfileSensitiveData
          ? form.document.profilDaten?.adresse
            ? Collapse({
                heading: 'Adresse',
                open: cardOpenStates.adresse,
                onToggle: open => setCardOpen('adresse', open),
                content: AddressForm({
                  form,
                  streetAndNumberFieldPath: ['profilDaten', 'adresse', 'streetAndNumber'],
                  zipFieldPath: ['profilDaten', 'adresse', 'zip'],
                  cityFieldPath: ['profilDaten', 'adresse', 'city'],
                  countryFieldPath: ['profilDaten', 'adresse', 'country'],
                  stateFieldPath: ['profilDaten', 'adresse', 'state'],
                  attentionLineFieldPath: ['profilDaten', 'adresse', 'attentionLine'],
                  auslandsPlzFieldPath: ['profilDaten', 'adresse', 'auslandsPlz'],
                  districtFieldPath: ['profilDaten', 'adresse', 'district'],
                }),
              })
            : canEditProfile
              ? html`
                  <button
                    class="btn btn-dash btn-block justify-start"
                    @click=${() => {
                      if (!form.document.profilDaten) return;

                      form.document.profilDaten.adresse = new Adresse({});
                      setCardOpen('adresse', true);
                    }}
                  >
                    <span class="icon-[material-symbols--add] text-success"></span>
                    Adresse hinzufügen
                  </button>
                `
              : nothing
          : nothing
      }
      ${
        canViewBankdata
          ? form.document.profilDaten?.bankInfos
            ? Collapse({
                heading: 'Bankdaten',
                open: cardOpenStates.bankInfos,
                onToggle: open => setCardOpen('bankInfos', open),
                content: BankDatenForm({ form }),
              })
            : canEditProfile
              ? html`
                  <button
                    class="btn btn-dash btn-block justify-start"
                    @click=${() => {
                      if (!form.document.profilDaten) return;

                      form.document.profilDaten.bankInfos = new BankDaten({});
                      setCardOpen('bankInfos', true);
                    }}
                  >
                    <span class="icon-[material-symbols--add] text-success"></span>
                    Bankdaten hinzufügen
                  </button>
                `
              : nothing
          : nothing
      }
      ${PersonRelations({ personID: person._id })}
      ${
        canViewProfileSensitiveData
          ? Collapse({
              heading: 'Buchungen & Mitgliedschaften',
              open: cardOpenStates.buchungen,
              onToggle: open => setCardOpen('buchungen', open),
              content: () => SgUserGroupMemberships({ personID: person._id }),
            })
          : nothing
      }

      <div class="h-32"></div>
    </div>

    ${
      canEditProfile
        ? html`
            ${PageFooter({
              content: html`
                ${LastEditorInformation({ referenceIds: [props.personID] })}
                <button class="btn btn-primary btn-lg" @click=${handleSave}>
                  <span class="icon-[material-symbols--save]"></span>
                  Speichern
                </button>
              `,
            })}
          `
        : nothing
    }
  `;
});
